/**
 * Bridges the app's message catalogue into `@erpx/ui`.
 *
 * The design system keeps its own English strings ("Previous page", "Visible
 * columns") and takes translations through an injection seam rather than
 * importing vue-i18n itself — see `ui/src/lib/strings.ts` for why. This is the
 * app side of that seam: one computed, provided once at the root, so a locale
 * switch re-renders the chrome inside every table, dialog and palette without
 * remounting anything.
 *
 * The key list is deliberately identical to the tenant app's bridge: the two
 * consoles share the component library, so they must not disagree about what
 * "Next page" is called in the catalogue.
 */

import type { UiStrings } from '@erpx/ui'
import { computed, type ComputedRef } from 'vue'

import { i18n } from '@/i18n'

export function uiStringsBundle(): ComputedRef<UiStrings> {
  return computed<UiStrings>(() => {
    // Read the locale so the bundle recomputes when it changes; `t` itself is
    // bound to the global scope and is not reactive on its own.
    void i18n.global.locale.value
    const t = i18n.global.t
    // `@erpx/ui` fills these placeholders itself; passing each name back as its
    // own "{name}" keeps it in vue-i18n's output instead of interpolating "".
    const keep = (key: string, ...names: string[]) =>
      t(key, Object.fromEntries(names.map((n) => [n, `{${n}}`])))

    return {
      buttonWorking: t('ui.button.working'),

      dialogClose: t('common.actions.close'),
      toastDismiss: t('common.actions.dismiss'),

      fieldRequired: t('ui.field.required'),
      fieldOptional: t('ui.field.optional'),

      moneyNegative: keep('ui.money.negative', 'amount'),

      selectPlaceholder: t('ui.select.placeholder'),
      selectClear: t('ui.select.clear'),

      paginationLabel: t('ui.pagination.label'),
      paginationPrevious: t('common.actions.previous'),
      paginationPreviousPage: t('ui.pagination.previousPage'),
      paginationNext: t('common.actions.next'),
      paginationNextPage: t('ui.pagination.nextPage'),
      paginationEmpty: t('ui.pagination.empty'),
      paginationShowing: keep('ui.pagination.showing', 'range'),
      paginationShowingOfTotal: keep('ui.pagination.showingOfTotal', 'range', 'total'),
      paginationPerPage: keep('ui.pagination.perPage', 'size'),

      // `@erpx/ui` has no plural engine, so the catalogue exposes the two forms
      // as separate keys and the table picks between them.
      tableRecordCountOne: keep('ui.table.recordCountOne', 'count'),
      tableRecordCountOther: keep('ui.table.recordCountOther', 'count'),
      tableColumns: t('ui.table.columns'),
      tableChooseColumns: t('ui.table.chooseColumns'),
      tableVisibleColumns: t('ui.table.visibleColumns'),
      tableSelectAllRows: t('ui.table.selectAllRows'),
      tableSelectRow: keep('ui.table.selectRow', 'index'),
      tableEmptyTitle: t('ui.table.emptyTitle'),
      tableFilteredTitle: t('ui.table.filteredTitle'),
      tableFilteredDescription: t('ui.table.filteredDescription'),
      tableClearFilters: t('common.actions.clearFilters'),

      panelEmptyTitle: t('ui.panel.emptyTitle'),
      panelLoading: t('common.labels.loading'),

      paletteTitle: t('palette.title'),
      paletteListLabel: t('palette.listLabel'),
      palettePlaceholder: t('palette.placeholder'),
      paletteEmpty: t('palette.empty'),
      paletteNavigateHint: t('palette.hint.navigate'),
      paletteRunHint: t('palette.hint.run'),
      paletteCloseHint: t('palette.hint.close'),
      paletteEscKey: t('palette.hint.escKey'),
    }
  })
}
